import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Download, FileText, Filter, ExternalLink, ChevronLeft,
  Building2, Users, BarChart3, ClipboardList, ChevronDown, ChevronRight, Eye,
  Plus, Trash2, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateTimeMdY } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const REPORT_TYPES = [
  { value: 'monthly', label: 'Generate Monthly Report', monthsRequired: 1 },
  { value: 'bi_monthly', label: 'Generate Bi-Monthly Report', monthsRequired: 2 },
  { value: 'quarterly', label: 'Generate Quarterly Report', monthsRequired: 4 },
  { value: 'biannually', label: 'Generate Biannually Report', monthsRequired: 6 },
  { value: 'annual', label: 'Generate Annual Report', monthsRequired: 0 },
];

const REPORT_TYPE_LABELS = {
  monthly: 'Monthly',
  bi_monthly: 'Bi-Monthly',
  quarterly: 'Quarterly',
  biannually: 'Biannually',
  annual: 'Annual',
};

const formatDate = (ds) => formatDateTimeMdY(ds);

const formatFileSize = (n) => {
  const val = Number(n) || 0;
  if (val < 1024) return `${val} B`;
  if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
  return `${(val / (1024 * 1024)).toFixed(1)} MB`;
};

const formatUsd = (n) => {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
};

const HUB_ACCENT_STYLES = {
  violet: {
    card: 'hover:border-violet-500/30',
    iconBox: 'border-violet-500/30 bg-violet-500/10',
    icon: 'text-violet-400',
  },
  blue: {
    card: 'hover:border-blue-500/30',
    iconBox: 'border-blue-500/30 bg-blue-500/10',
    icon: 'text-blue-400',
  },
  teal: {
    card: 'hover:border-teal-500/30',
    iconBox: 'border-teal-500/30 bg-teal-500/10',
    icon: 'text-teal-400',
  },
};

function HubSectionCard({ title, description, count, icon: Icon, onClick, testId, accent = 'violet' }) {
  const styles = HUB_ACCENT_STYLES[accent] || HUB_ACCENT_STYLES.violet;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left bg-white/5 border border-white/10 hover:bg-white/[0.07] ${styles.card} p-6 transition-colors group`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className={`w-10 h-10 flex items-center justify-center border mb-4 ${styles.iconBox}`}>
            <Icon className={`w-5 h-5 ${styles.icon}`} />
          </div>
          <h2 className="text-lg font-['Unbounded'] font-semibold text-white mb-2">{title}</h2>
          <p className="text-sm font-['Manrope'] text-white/50">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/50 mt-1 shrink-0" />
      </div>
      {count != null && (
        <p className="text-xs font-['Manrope'] text-white/35 mt-4">{count}</p>
      )}
    </button>
  );
}

function GenerateReportModal({ token, reportType, onClose, onSuccess }) {
  const headers = { Authorization: `Bearer ${token}` };
  const config = REPORT_TYPES.find((r) => r.value === reportType);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleMonth = (month) => {
    if (!config || config.monthsRequired === 0) return;
    setSelectedMonths((prev) => {
      if (prev.includes(month)) return prev.filter((m) => m !== month);
      if (prev.length >= config.monthsRequired) {
        toast.error(`Select exactly ${config.monthsRequired} month(s) for this report`);
        return prev;
      }
      return [...prev, month].sort((a, b) => a - b);
    });
  };

  const handleSubmit = async () => {
    if (!config) return;
    if (config.monthsRequired > 0 && selectedMonths.length !== config.monthsRequired) {
      toast.error(`Please select exactly ${config.monthsRequired} month(s)`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/reports/generate`,
        {
          report_type: reportType,
          year,
          months: config.monthsRequired > 0 ? selectedMonths : [],
        },
        { headers },
      );
      toast.success('Report generated successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setSubmitting(false);
    }
  };

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 10; y -= 1) yearOptions.push(y);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 max-lg:p-0 bg-black/80 backdrop-blur-sm" data-testid="generate-report-modal">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[85vh] max-lg:max-h-none max-lg:h-[100dvh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-['Unbounded'] font-bold text-white">{config?.label || 'Generate Report'}</h2>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Year *</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {config?.monthsRequired > 0 ? (
            <div>
              <label className="block text-xs font-['Manrope'] text-white/60 mb-2">
                Select {config.monthsRequired} month{config.monthsRequired > 1 ? 's' : ''} *
              </label>
              <div className="grid grid-cols-2 gap-2">
                {MONTH_OPTIONS.map((m) => (
                  <label key={m.value} className="flex items-center gap-2 cursor-pointer px-2 py-1.5 border border-white/10 hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={selectedMonths.includes(m.value)}
                      onChange={() => toggleMonth(m.value)}
                      className="accent-violet-500"
                    />
                    <span className="text-sm font-['Manrope'] text-white/80">{m.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] font-['Manrope'] text-white/35 mt-2">
                Selected: {selectedMonths.length} / {config.monthsRequired}
              </p>
            </div>
          ) : (
            <p className="text-sm font-['Manrope'] text-white/50">
              This report will summarize all sales and revenue for the full calendar year {year}.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">Cancel</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="generate-report-submit-btn"
            className="px-5 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
          >
            {submitting ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { token, isDeveloper } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [view, setView] = useState('hub');
  const [hubSummary, setHubSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [reports, setReports] = useState([]);
  const [st556Docs, setSt556Docs] = useState([]);
  const [customerDeals, setCustomerDeals] = useState([]);
  const [loadingSection, setLoadingSection] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [expandedDeals, setExpandedDeals] = useState({});

  const [showReportMenu, setShowReportMenu] = useState(false);
  const [reportModalType, setReportModalType] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', language: 'en', file: null });
  const [templateSaving, setTemplateSaving] = useState(false);

  const fetchHubSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/hub-summary`, { headers });
      setHubSummary(res.data);
    } catch {
      toast.error('Failed to load documents hub');
    } finally {
      setLoadingSummary(false);
    }
  }, [headers]);

  const fetchReports = useCallback(async () => {
    setLoadingSection(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/reports`, { headers });
      setReports(res.data.reports || []);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoadingSection(false);
    }
  }, [headers]);

  const fetchSt556 = useCallback(async () => {
    setLoadingSection(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/internal/st556`, { headers });
      setSt556Docs(res.data.documents || []);
    } catch {
      toast.error('Failed to load ST-556 worksheets');
    } finally {
      setLoadingSection(false);
    }
  }, [headers]);

  const fetchTemplates = useCallback(async () => {
    if (!isDeveloper) return;
    setLoadingSection(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/templates`, { headers });
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoadingSection(false);
    }
  }, [headers, isDeveloper]);

  const fetchCustomerDocs = useCallback(async () => {
    setLoadingSection(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/customer`, { headers });
      setCustomerDeals(res.data.deals || []);
    } catch {
      toast.error('Failed to load customer documents');
    } finally {
      setLoadingSection(false);
    }
  }, [headers]);

  useEffect(() => { fetchHubSummary(); }, [fetchHubSummary]);
  useEffect(() => { if (isDeveloper) fetchTemplates(); }, [isDeveloper, fetchTemplates]);

  useEffect(() => {
    if (view === 'internal-reports') fetchReports();
    if (view === 'internal-st556') fetchSt556();
    if (view === 'customer') fetchCustomerDocs();
    if (view === 'templates') fetchTemplates();
  }, [view, fetchReports, fetchSt556, fetchCustomerDocs, fetchTemplates]);

  const reportTypeNames = [...new Set(reports.map((r) => r.report_type).filter(Boolean))];

  const filteredReports = reports.filter((report) => {
    if (templateFilter !== 'all' && report.report_type !== templateFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        report.period_label?.toLowerCase().includes(q)
        || report.report_type?.toLowerCase().includes(q)
        || report.id?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const filteredSt556 = st556Docs.filter((doc) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        doc.deal_customer?.toLowerCase().includes(q)
        || doc.deal_id?.toLowerCase().includes(q)
        || doc.id?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const filteredCustomerDeals = customerDeals.filter((deal) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        deal.customer_name?.toLowerCase().includes(q)
        || deal.deal_id?.toLowerCase().includes(q)
        || (deal.documents || []).some((d) => d.template_name?.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const handleDownloadDoc = async (docId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/${docId}/download`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `document_${docId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleViewDoc = async (docId) => {
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('Pop-up blocked. Allow pop-ups to view the document.');
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/${docId}/view`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab.close();
      toast.error('Failed to open document');
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/reports/${reportId}/download`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${reportId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleViewReport = async (reportId) => {
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('Pop-up blocked. Allow pop-ups to view the report.');
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/reports/${reportId}/view`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab.close();
      toast.error('Failed to open report');
    }
  };

  const handleViewTemplate = async (tmpl) => {
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('Pop-up blocked. Allow pop-ups to view the template.');
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/documents/templates/file`, {
        headers,
        params: { name: tmpl.name, language: tmpl.language || 'en' },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab.close();
      toast.error('Failed to open template');
    }
  };

  const handleDeleteTemplate = async (tmpl) => {
    if (!window.confirm(`Delete template "${tmpl.display_name || tmpl.name}" (${(tmpl.language || 'en').toUpperCase()})?`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/documents/templates`, {
        headers,
        params: { name: tmpl.name, language: tmpl.language || 'en' },
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!templateForm.file) {
      toast.error('Choose a PDF file');
      return;
    }
    setTemplateSaving(true);
    try {
      const data = new FormData();
      data.append('file', templateForm.file);
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/documents/upload-template`,
        data,
        {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          params: {
            name: templateForm.name.trim(),
            language: templateForm.language,
          },
        }
      );
      toast.success(res.data?.status === 'updated' ? 'Template replaced' : 'Template added');
      setTemplateForm({ name: '', language: 'en', file: null });
      setShowAddTemplate(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const goHub = () => {
    setView('hub');
    setSearchTerm('');
    setTemplateFilter('all');
    fetchHubSummary();
  };

  const renderBreadcrumb = (segments) => (
    <div className="flex items-center gap-2 text-sm font-['Manrope'] text-white/40 mb-4">
      {segments.map((seg, i) => (
        <React.Fragment key={seg.label}>
          {i > 0 && <span>/</span>}
          {seg.onClick ? (
            <button type="button" onClick={seg.onClick} className="hover:text-white transition-colors">{seg.label}</button>
          ) : (
            <span className="text-white/70">{seg.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderFilters = ({ showTemplateFilter, templateOptions, placeholder }) => (
    <div className="bg-white/5 border border-white/10 p-4 mb-4">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/5 border border-white/10 text-white text-sm pl-10 pr-4 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
          />
        </div>
        {showTemplateFilter && (
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <select
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              data-testid="docs-template-filter"
              className="bg-white/5 border border-white/10 text-white text-sm pl-10 pr-8 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 min-w-[180px]"
            >
              <option value="all">All Templates</option>
              {templateOptions.map((n) => (
                <option key={n} value={n}>{REPORT_TYPE_LABELS[n] || n}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="documents-page">
      <div className="mb-6">
        <h1 className="text-2xl font-['Unbounded'] font-bold text-white" data-testid="documents-page-title">Documents</h1>
        <p className="text-sm font-['Manrope'] text-white/40 mt-1">
          Internal dealership documents and customer-related deal paperwork
        </p>
      </div>

      {view === 'hub' && (
        <>
          {loadingSummary ? (
            <div className="p-12 text-center text-white/40 font-['Manrope']">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="documents-hub-sections">
              <HubSectionCard
                title="All Internal Documents"
                description="Dealership reports and ST-556 tax worksheets"
                count={`${hubSummary?.internal_report_count || 0} reports · ${hubSummary?.st556_count || 0} ST-556 worksheets`}
                icon={Building2}
                accent="violet"
                testId="internal-documents-section"
                onClick={() => setView('internal')}
              />
              <HubSectionCard
                title="All Customer Related Documents"
                description="Generated closing packets and customer forms across all deals"
                count={`${hubSummary?.customer_document_count || 0} documents across ${hubSummary?.deal_count_with_customer_docs || 0} deals`}
                icon={Users}
                accent="blue"
                testId="customer-documents-section"
                onClick={() => setView('customer')}
              />
              {isDeveloper && (
                <HubSectionCard
                  title="Document Templates"
                  description="View, add, or replace the blank PDFs used for deal document generation"
                  count={`${templates.length || hubSummary?.template_count || 0} stored templates`}
                  icon={FileText}
                  accent="teal"
                  testId="document-templates-section"
                  onClick={() => setView('templates')}
                />
              )}
            </div>
          )}
        </>
      )}

      {view === 'internal' && (
        <>
          <button type="button" onClick={goHub} className="flex items-center gap-1 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Documents Hub
          </button>
          {renderBreadcrumb([
            { label: 'Documents', onClick: goHub },
            { label: 'All Internal Documents' },
          ])}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <HubSectionCard
              title="All Reports"
              description="Sales and revenue summary reports by period"
              count={`${hubSummary?.internal_report_count || 0} generated reports`}
              icon={BarChart3}
              accent="violet"
              testId="all-reports-section"
              onClick={() => setView('internal-reports')}
            />
            <HubSectionCard
              title="ST-556 Worksheets"
              description="Internal ST-556 tax filing worksheets from delivered deals"
              count={`${hubSummary?.st556_count || 0} worksheets`}
              icon={ClipboardList}
              accent="teal"
              testId="all-st556-section"
              onClick={() => setView('internal-st556')}
            />
          </div>
        </>
      )}

      {view === 'internal-reports' && (
        <>
          <button type="button" onClick={() => { setView('internal'); setSearchTerm(''); setTemplateFilter('all'); }} className="flex items-center gap-1 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Internal Documents
          </button>
          {renderBreadcrumb([
            { label: 'Documents', onClick: goHub },
            { label: 'Internal', onClick: () => setView('internal') },
            { label: 'All Reports' },
          ])}

          <div className="bg-white/5 border border-white/10 p-4 mb-4">
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Search reports by period or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm pl-10 pr-4 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <select
                  value={templateFilter}
                  onChange={(e) => setTemplateFilter(e.target.value)}
                  data-testid="docs-template-filter"
                  className="bg-white/5 border border-white/10 text-white text-sm pl-10 pr-8 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 min-w-[180px]"
                >
                  <option value="all">All Templates</option>
                  {reportTypeNames.map((n) => (
                    <option key={n} value={n}>{REPORT_TYPE_LABELS[n] || n}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowReportMenu((v) => !v)}
                  data-testid="generate-reports-btn"
                  className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold whitespace-nowrap"
                >
                  Generate Reports
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showReportMenu && (
                  <div className="absolute right-0 top-full mt-1 z-20 min-w-[240px] bg-[#0a0a0a] border border-white/10 shadow-xl">
                    {REPORT_TYPES.map((rt) => (
                      <button
                        key={rt.value}
                        type="button"
                        onClick={() => { setReportModalType(rt.value); setShowReportMenu(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm font-['Manrope'] text-white/80 hover:bg-white/5 hover:text-white"
                      >
                        {rt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 overflow-hidden" data-testid="reports-table">
            {loadingSection ? (
              <div className="p-12 text-center text-white/40 font-['Manrope']">Loading reports...</div>
            ) : filteredReports.length === 0 ? (
              <div className="p-12 text-center">
                <BarChart3 className="w-10 h-10 text-white/10 mx-auto mb-3" />
                <p className="text-white/40 font-['Manrope'] text-sm">No reports yet. Use Generate Reports to create one.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      {['Report', 'Period', 'Completed Sales', 'Gross PVR', 'Revenue', 'Generated', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-[11px] font-['Manrope'] font-semibold text-white/60 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {filteredReports.map((report) => (
                      <tr key={report.id} className="hover:bg-white/[0.03]">
                        <td className="px-4 py-3 font-['Manrope'] text-sm text-white">
                          {REPORT_TYPE_LABELS[report.report_type] || report.report_label}
                        </td>
                        <td className="px-4 py-3 font-['Manrope'] text-sm text-white/70">{report.period_label}</td>
                        <td className="px-4 py-3 font-['Manrope'] text-sm text-white/60">
                          {report.summary?.completed_sales_count ?? '-'}
                        </td>
                        <td className="px-4 py-3 font-['Manrope'] text-sm text-violet-300">
                          {report.summary?.operations?.gross_pvr != null
                            ? formatUsd(report.summary.operations.gross_pvr)
                            : '-'}
                        </td>
                        <td className="px-4 py-3 font-['Manrope'] text-sm text-emerald-400">
                          {formatUsd(report.summary?.total_sale_revenue)}
                        </td>
                        <td className="px-4 py-3 font-['Manrope'] text-xs text-white/40">{formatDate(report.generated_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleViewReport(report.id)}
                              className="p-1.5 hover:bg-white/10"
                              title="View report"
                              data-testid={`view-report-${report.id}`}
                            >
                              <Eye className="w-4 h-4 text-sky-400" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadReport(report.id)}
                              className="p-1.5 hover:bg-white/10"
                              title="Download"
                              data-testid={`download-report-${report.id}`}
                            >
                              <Download className="w-4 h-4 text-violet-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {view === 'internal-st556' && (
        <>
          <button type="button" onClick={() => { setView('internal'); setSearchTerm(''); }} className="flex items-center gap-1 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Internal Documents
          </button>
          {renderBreadcrumb([
            { label: 'Documents', onClick: goHub },
            { label: 'Internal', onClick: () => setView('internal') },
            { label: 'ST-556 Worksheets' },
          ])}
          {renderFilters({ showTemplateFilter: false, placeholder: 'Search by customer or deal ID...' })}

          <div className="bg-white/5 border border-white/10 overflow-hidden">
            {loadingSection ? (
              <div className="p-12 text-center text-white/40 font-['Manrope']">Loading ST-556 worksheets...</div>
            ) : filteredSt556.length === 0 ? (
              <div className="p-12 text-center text-white/40 font-['Manrope'] text-sm">No ST-556 worksheets generated yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      {['Customer / Deal', 'Deal Status', 'Generated', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-[11px] font-['Manrope'] font-semibold text-white/60 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {filteredSt556.map((doc) => (
                      <tr key={doc.id} className="hover:bg-white/[0.03]">
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => navigate(`/admin/deals/${doc.deal_id}`)} className="text-sm font-['Manrope'] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                            {doc.deal_customer || doc.deal_id?.slice(0, 8)}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm font-['Manrope'] text-white/60">{(doc.deal_status || '').replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-xs font-['Manrope'] text-white/40">{formatDate(doc.generated_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleViewDoc(doc.id)}
                              className="p-1.5 hover:bg-white/10"
                              title="View worksheet"
                              data-testid={`view-st556-${doc.id}`}
                            >
                              <Eye className="w-4 h-4 text-sky-400" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadDoc(doc.id)}
                              className="p-1.5 hover:bg-white/10"
                              title="Download"
                              data-testid={`download-st556-${doc.id}`}
                            >
                              <Download className="w-4 h-4 text-teal-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {view === 'customer' && (
        <>
          <button type="button" onClick={goHub} className="flex items-center gap-1 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Documents Hub
          </button>
          {renderBreadcrumb([
            { label: 'Documents', onClick: goHub },
            { label: 'All Customer Related Documents' },
          ])}
          {renderFilters({ showTemplateFilter: false, placeholder: 'Search by customer, deal, or template...' })}

          <div className="space-y-3" data-testid="customer-documents-list">
            {loadingSection ? (
              <div className="p-12 text-center text-white/40 font-['Manrope']">Loading customer documents...</div>
            ) : filteredCustomerDeals.length === 0 ? (
              <div className="bg-white/5 border border-white/10 p-12 text-center text-white/40 font-['Manrope'] text-sm">
                No customer documents generated yet.
              </div>
            ) : (
              filteredCustomerDeals.map((deal) => {
                const expanded = Boolean(expandedDeals[deal.deal_id]);
                return (
                  <div key={deal.deal_id} className="bg-white/5 border border-white/10 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedDeals((prev) => ({ ...prev, [deal.deal_id]: !prev[deal.deal_id] }))}
                      className="w-full flex items-center justify-between px-4 py-4 hover:bg-white/[0.03] text-left"
                    >
                      <div className="flex items-center gap-3">
                        {expanded ? <ChevronDown className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
                        <div>
                          <p className="font-['Manrope'] font-semibold text-white">{deal.customer_name}</p>
                          <p className="text-xs font-['Manrope'] text-white/40 mt-0.5">
                            {deal.document_count} document{deal.document_count === 1 ? '' : 's'}
                            {deal.deal_status ? ` · ${deal.deal_status.replace(/_/g, ' ')}` : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/deals/${deal.deal_id}`); }}
                        className="text-xs font-['Manrope'] text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        View Deal <ExternalLink className="w-3 h-3" />
                      </button>
                    </button>
                    {expanded && (
                      <div className="border-t border-white/10">
                        <table className="w-full">
                          <thead className="bg-white/[0.03]">
                            <tr>
                              {['Template', 'Language', 'Generated', 'Status', ''].map((h) => (
                                <th key={h || 'actions'} className="px-4 py-2 text-left text-[10px] font-['Manrope'] font-semibold text-white/50 uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/[0.06]">
                            {(deal.documents || []).map((doc) => (
                              <tr key={doc.id} className="hover:bg-white/[0.02]">
                                <td className="px-4 py-2 text-sm font-['Manrope'] text-white">{doc.template_name}</td>
                                <td className="px-4 py-2 text-sm text-white/60">{doc.language?.toUpperCase()}</td>
                                <td className="px-4 py-2 text-xs text-white/40">{formatDate(doc.generated_at)}</td>
                                <td className="px-4 py-2">
                                  <span className="px-2 py-0.5 text-[10px] bg-green-500/15 text-green-400 border border-green-500/30">
                                    {doc.display_status || doc.status || 'draft'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleViewDoc(doc.id)}
                                      className="p-1 hover:bg-white/10"
                                      title="View document"
                                      data-testid={`view-customer-doc-${doc.id}`}
                                    >
                                      <Eye className="w-4 h-4 text-sky-400" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadDoc(doc.id)}
                                      className="p-1 hover:bg-white/10"
                                      title="Download"
                                      data-testid={`download-customer-doc-${doc.id}`}
                                    >
                                      <Download className="w-4 h-4 text-blue-400" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {view === 'templates' && isDeveloper && (
        <>
          <button type="button" onClick={goHub} className="flex items-center gap-1 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Documents Hub
          </button>
          {renderBreadcrumb([
            { label: 'Documents', onClick: goHub },
            { label: 'Document Templates' },
          ])}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-['Manrope'] text-white/45">
              Names must match the keys used in document generation (for example <span className="text-white/70">bill_of_sale</span>).
              Uploading the same name and language replaces the stored PDF.
            </p>
            <button
              type="button"
              onClick={() => setShowAddTemplate((v) => !v)}
              className="shrink-0 flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-['Manrope'] font-semibold"
              data-testid="add-template-toggle"
            >
              <Plus className="w-4 h-4" />
              {showAddTemplate ? 'Close' : 'Add Template'}
            </button>
          </div>

          {showAddTemplate && (
            <form onSubmit={handleAddTemplate} className="bg-white/5 border border-white/10 p-5 mb-4 space-y-4" data-testid="add-template-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Template name *</label>
                  <input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 text-sm font-['Manrope'] focus:outline-none focus:border-teal-500/50"
                    placeholder="bill_of_sale"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">Language</label>
                  <select
                    value={templateForm.language}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, language: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 text-sm font-['Manrope'] focus:outline-none focus:border-teal-500/50 [&>option]:bg-gray-900"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">PDF file *</label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setTemplateForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                    className="w-full text-sm font-['Manrope'] text-white/70 file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-white/10 file:text-white"
                    data-testid="template-file-input"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={templateSaving}
                  className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {templateSaving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </form>
          )}

          <div className="bg-white/5 border border-white/10 overflow-hidden">
            {loadingSection ? (
              <div className="p-12 text-center text-white/40 font-['Manrope']">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="p-12 text-center text-white/40 font-['Manrope'] text-sm">No templates stored yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      {['Template', 'Key', 'Language', 'Uploaded', 'Size', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-[11px] font-['Manrope'] font-semibold text-white/60 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {templates.map((tmpl) => (
                      <tr key={`${tmpl.name}-${tmpl.language}`} className="hover:bg-white/[0.03]">
                        <td className="px-4 py-3 text-sm font-['Manrope'] text-white">{tmpl.display_name || tmpl.name}</td>
                        <td className="px-4 py-3 text-xs font-mono text-white/55">{tmpl.name}</td>
                        <td className="px-4 py-3 text-sm font-['Manrope'] text-white/70">{(tmpl.language || 'en').toUpperCase()}</td>
                        <td className="px-4 py-3 text-xs font-['Manrope'] text-white/40">{formatDate(tmpl.uploaded_at)}</td>
                        <td className="px-4 py-3 text-xs font-['Manrope'] text-white/40">{tmpl.file_size ? formatFileSize(tmpl.file_size) : '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => handleViewTemplate(tmpl)} className="p-1.5 hover:bg-white/10" title="View PDF">
                              <Eye className="w-4 h-4 text-sky-400" />
                            </button>
                            <button type="button" onClick={() => handleDeleteTemplate(tmpl)} className="p-1.5 hover:bg-white/10" title="Delete">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {reportModalType && (
        <GenerateReportModal
          token={token}
          reportType={reportModalType}
          onClose={() => setReportModalType(null)}
          onSuccess={() => {
            fetchReports();
            fetchHubSummary();
          }}
        />
      )}
    </div>
  );
}